import { useEffect, useMemo, useState } from 'react';

import { classesApi } from '../../services/api';
import type { Gradebook } from '../../types';
import { cn } from '../../utils/cn';
import { formatNumber } from '../../utils/gradeSheet';
import {
    CELL_TONE_CLASSES,
    completionTone,
    dateColumnLabel,
    homeworkCellView,
    scoreCellView,
    type CellView,
} from '../../utils/gradebookView';
import { formatDateKey } from '../../utils/money';
import { PayrollRangePicker, defaultPayrollRange, type PayrollRange } from '../payroll/PayrollRangePicker';
import { EmptyState, LoadingState, SearchSelect, useToast } from '../ui';

type Sheet = 'homework' | 'tests';

const SHEETS: Array<{ value: Sheet; label: string }> = [
    { value: 'homework', label: 'Bài tập về nhà' },
    { value: 'tests', label: 'Điểm kiểm tra' },
];

const headCell = 'border-b border-ink-200 bg-ink-50 px-2 py-2 text-center text-xs font-medium text-ink-600';
const nameHead = 'sticky left-0 z-20 w-full min-w-[8rem] border-b border-r border-ink-200 bg-ink-50 px-3 py-2 text-left text-xs font-medium text-ink-600 sm:min-w-[11rem]';
const nameCell = 'sticky left-0 z-10 border-r border-ink-200 bg-white px-3 py-2 text-left font-medium text-ink-900';
const totalHead = 'border-b border-l border-ink-200 bg-ink-100 px-2 py-2 text-center text-xs font-semibold text-ink-700';

function ViewCell({ view, className }: { view: CellView; className?: string }) {
    return (
        <td title={view.title} className={cn('whitespace-nowrap px-2 py-2 text-center tabular-nums', CELL_TONE_CLASSES[view.tone], className)}>
            {view.text}
        </td>
    );
}

function DateHead({ dateKey }: { dateKey: string }) {
    const label = dateColumnLabel(dateKey);
    return (
        <th scope="col" className={cn(headCell, 'min-w-[4.75rem]')}>
            <span className="block font-semibold text-ink-900">{label.day}</span>
            <span className="block font-normal">{label.weekday}</span>
        </th>
    );
}

// Sổ điểm chỉ để xem; muốn sửa thì vào đúng buổi học ở trang Buổi học.
export function ClassGradebook({ classId }: { classId: string }) {
    const { showToast } = useToast();
    const [sheet, setSheet] = useState<Sheet>('homework');
    const [range, setRange] = useState<PayrollRange>(defaultPayrollRange);
    const [studentId, setStudentId] = useState('all');
    const [data, setData] = useState<Gradebook | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        classesApi.getGradebook(classId, range)
            .then(response => { if (!cancelled) setData(response.data.data); })
            .catch((error: any) => { if (!cancelled) showToast(error?.message || 'Không tải được sổ điểm.', 'error'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [classId, range, showToast]);

    const rows = useMemo(
        () => (data?.rows ?? []).filter(row => studentId === 'all' || row.student.id === studentId),
        [data, studentId],
    );
    const single = studentId !== 'all' ? rows[0] : null;

    return (
        <section className="space-y-3" aria-label="Sổ điểm">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="inline-flex rounded-lg border border-ink-200 bg-white p-0.5" role="tablist" aria-label="Sổ điểm">
                    {SHEETS.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            role="tab"
                            aria-selected={sheet === option.value}
                            onClick={() => setSheet(option.value)}
                            className={cn(
                                'min-h-8 rounded px-3 text-sm transition-colors',
                                sheet === option.value ? 'bg-ink-100 font-semibold text-ink-900' : 'text-ink-600 hover:text-ink-900',
                            )}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <PayrollRangePicker value={range} onChange={setRange} />
                    <SearchSelect
                        aria-label="Học sinh"
                        value={studentId === 'all' ? '' : studentId}
                        onChange={value => setStudentId(value || 'all')}
                        emptyOption="Tất cả học sinh"
                        options={(data?.rows ?? []).map(row => ({ value: row.student.id, label: row.student.name }))}
                        className="w-48"
                    />
                </div>
            </div>

            {loading && !data ? <LoadingState title="Đang tải sổ điểm..." /> : null}
            {!loading && !data ? <EmptyState title="Không tải được sổ điểm" /> : null}
            {data && data.rows.length === 0 ? <EmptyState title="Lớp chưa có học sinh" /> : null}

            {data && data.rows.length > 0 && sheet === 'homework' ? (
                data.dates.length === 0 ? (
                    <EmptyState title="Không có buổi học nào trong khoảng này" description="Thử chọn tháng khác." />
                ) : single ? (
                    <div className="overflow-x-auto rounded-lg border border-ink-200">
                        <table className="w-full text-sm">
                            <thead>
                                <tr>
                                    <th scope="col" className={cn(headCell, 'text-left')}>Buổi học</th>
                                    <th scope="col" className={headCell}>Đúng/Đã làm/Được giao</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-ink-100">
                                {data.dates.map((date, index) => {
                                    const label = dateColumnLabel(date.dateKey);
                                    return (
                                        <tr key={date.dateKey}>
                                            <td className="px-3 py-2 text-ink-900">{label.weekday}, {formatDateKey(date.dateKey)}</td>
                                            <ViewCell view={homeworkCellView(single.homework[index])} />
                                        </tr>
                                    );
                                })}
                                <tr className="bg-ink-50 font-semibold">
                                    <td className="px-3 py-2 text-ink-900">Tổng</td>
                                    <td className="px-2 py-2 text-center tabular-nums text-ink-900">
                                        {formatNumber(single.homeworkTotals.correct)}/{formatNumber(single.homeworkTotals.completed)}/{formatNumber(single.homeworkTotals.assigned)}
                                        {single.homeworkTotals.completionPercent !== null ? ` · ${formatNumber(single.homeworkTotals.completionPercent)}%` : ''}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={cn('overflow-auto rounded-lg border border-ink-200 sm:max-h-[60vh]', loading && 'opacity-60')}>
                        <table className="w-full border-separate border-spacing-0 text-sm">
                            <thead className="sticky top-0 z-30">
                                <tr>
                                    <th scope="col" className={nameHead}>Học sinh</th>
                                    {data.dates.map(date => <DateHead key={date.dateKey} dateKey={date.dateKey} />)}
                                    <th scope="col" className={cn(totalHead, 'min-w-[5.5rem]')}>Tổng</th>
                                    <th scope="col" className={cn(totalHead, 'min-w-[5.5rem] border-l-0')}>% làm bài</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(row => (
                                    <tr key={row.student.id} className="[&>td]:border-b [&>td]:border-ink-100">
                                        <td className={nameCell}>{row.student.name}</td>
                                        {row.homework.map((cell, index) => (
                                            <ViewCell key={data.dates[index].dateKey} view={homeworkCellView(cell)} />
                                        ))}
                                        <td className="whitespace-nowrap border-l border-ink-200 bg-ink-50 px-2 py-2 text-center font-semibold tabular-nums text-ink-900">
                                            {formatNumber(row.homeworkTotals.correct)}/{formatNumber(row.homeworkTotals.completed)}/{formatNumber(row.homeworkTotals.assigned)}
                                        </td>
                                        <ViewCell
                                            className="bg-ink-50 font-semibold"
                                            view={{
                                                text: row.homeworkTotals.completionPercent === null ? '—' : `${formatNumber(row.homeworkTotals.completionPercent)}%`,
                                                tone: completionTone(row.homeworkTotals.completionPercent),
                                            }}
                                        />
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : null}

            {data && data.rows.length > 0 && sheet === 'tests' ? (
                data.assessments.length === 0 ? (
                    <EmptyState title="Chưa có bài kiểm tra nào trong khoảng này" description="Bài kiểm tra được thêm ở tab Điểm kiểm tra của từng buổi học." />
                ) : single ? (
                    <div className="overflow-x-auto rounded-lg border border-ink-200">
                        <table className="w-full text-sm">
                            <thead>
                                <tr>
                                    <th scope="col" className={cn(headCell, 'text-left')}>Bài kiểm tra</th>
                                    <th scope="col" className={headCell}>Hệ số</th>
                                    <th scope="col" className={headCell}>Điểm</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-ink-100">
                                {data.assessments.map((assessment, index) => (
                                    <tr key={assessment.id}>
                                        <td className="px-3 py-2">
                                            <span className="block text-ink-900">{assessment.name}</span>
                                            <span className="block text-xs text-ink-500">{formatDateKey(assessment.dateKey)}</span>
                                        </td>
                                        <td className="px-2 py-2 text-center tabular-nums text-ink-700">×{formatNumber(assessment.weight)}</td>
                                        <ViewCell view={scoreCellView(single.scores[index])} />
                                    </tr>
                                ))}
                                <tr className="bg-ink-50 font-semibold">
                                    <td className="px-3 py-2 text-ink-900" colSpan={2}>Điểm trung bình</td>
                                    <td className="px-2 py-2 text-center tabular-nums text-ink-900">{single.average === null ? '—' : formatNumber(single.average)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={cn('overflow-auto rounded-lg border border-ink-200 sm:max-h-[60vh]', loading && 'opacity-60')}>
                        <table className="w-full border-separate border-spacing-0 text-sm">
                            <thead className="sticky top-0 z-30">
                                <tr>
                                    <th scope="col" className={nameHead}>Học sinh</th>
                                    {data.assessments.map(assessment => (
                                        <th key={assessment.id} scope="col" title={assessment.name} className={cn(headCell, 'min-w-[7.5rem] max-w-[10rem]')}>
                                            <span className="block truncate font-semibold text-ink-900">{assessment.name}</span>
                                            <span className="block font-normal">{dateColumnLabel(assessment.dateKey).day} · ×{formatNumber(assessment.weight)}</span>
                                        </th>
                                    ))}
                                    <th scope="col" className={cn(totalHead, 'min-w-[6rem]')}>Trung bình</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(row => (
                                    <tr key={row.student.id} className="[&>td]:border-b [&>td]:border-ink-100">
                                        <td className={nameCell}>{row.student.name}</td>
                                        {row.scores.map((cell, index) => (
                                            <ViewCell key={data.assessments[index].id} view={scoreCellView(cell)} />
                                        ))}
                                        <td className="border-l border-ink-200 bg-ink-50 px-2 py-2 text-center font-semibold tabular-nums text-ink-900">
                                            {row.average === null ? '—' : formatNumber(row.average)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : null}

            {data && data.rows.length > 0 ? (
                <p className="text-xs text-ink-500">
                    {sheet === 'homework'
                        ? 'Ô ghi đúng/đã làm/được giao. Vắng tính 0/0/số bài được giao của buổi. “Chưa chấm”: có đi học nhưng chưa nhập. “Chưa ĐD”: chưa điểm danh. “—”: chưa tạo buổi học.'
                        : 'Thang điểm 10. Trung bình có nhân hệ số; bài vắng hoặc chưa có điểm không được tính.'}
                    {' '}Muốn sửa, mở đúng buổi học ở trang Buổi học.
                </p>
            ) : null}
        </section>
    );
}
